import React, { FC, useCallback } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useFileFormatUpload } from '../hooks/useFileFormatUpload'
import {
  DurationInput,
  FileErrorType
} from '../../types/campaign'

import {
  deriveCreativeNameFromFile,
  ensureUniqueCreativeName
} from '../utils'

export type FileUploadProps = {
  accept: string
  disabled: boolean
  orientation: string
  maxDuration: number
  aspectRatio: string
  duration: DurationInput
  flightId: number
  existingCreativeNames?: string[]
  onSuggestName?: (name: string) => void
  onFilePrepared?: (data: { file: File; mediaLength: number }) => void
  flightTimezone: string
  onUploadSuccess: (success: boolean) => void
  onUploadPending: (isPending: boolean) => void
  onUploadError: ({
    error,
    errorMessage
  }: {
    error: boolean
    errorMessage: string
  }) => void
}

const AssetUploader: FC<FileUploadProps> = ({
  accept = '.png, .jpg, .jpeg',
  disabled,
  orientation,
  maxDuration,
  aspectRatio,
  duration,
  flightId,
  existingCreativeNames = [],
  onSuggestName,
  onFilePrepared,
  onUploadError
}) => {
  const handleFileError = (
    fileErrorType: FileErrorType | 'clear-error',
    fileName: string
  ) => {
    if (fileErrorType === 'clear-error') {
      onUploadError({ error: false, errorMessage: '' })
      return
    }
    onUploadError({
      error: true,
      errorMessage: `Error: ${fileErrorType} for ${fileName}`
    })
  }

  const { handleFile } = useFileFormatUpload(handleFileError)

  const handleProcessedFile = useCallback(
    ({
      asset,
      mediaLength
    }: {
      asset: File
      mediaLength?: number
    }) => {
      // Suggest a default unique name based on the file name, without extension
      if (onSuggestName) {
        const baseName = deriveCreativeNameFromFile(asset.name)
        const uniqueName = ensureUniqueCreativeName(baseName, existingCreativeNames)
        onSuggestName(uniqueName)
      }

      // Pass the prepared file upwards for save-on-click outside this component
      if (onFilePrepared) {
        onFilePrepared({ file: asset, mediaLength: mediaLength ?? 0 })
      }
    },
    [existingCreativeNames, onFilePrepared, onSuggestName]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      handleFile(
        file,
        maxDuration,
        aspectRatio,
        duration,
        flightId,
        handleProcessedFile
      )
    }
    e.target.value = ''
  }

  return (
    <>
      <input
        onChange={handleChange}
        accept={accept}
        style={{ display: 'none' }}
        id="file-upload"
        type="file"
        disabled={disabled}
        data-testid="file-upload"
      />

      <Box
        data-testid="file-uploader-input"
        component={'label'}
        htmlFor="file-upload"
        gap="10px"
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'center',
          display: 'flex',
          border: 'none',
          backgroundImage:
            // eslint-disable-next-line
            "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23BFBFC9FF' stroke-width='3' stroke-dasharray='6%2c 8.3' stroke-dashoffset='1' stroke-linecap='round'/%3e%3c/svg%3e\")",
          backgroundColor: 'inherit',
          borderRadius: '8px',
          height: '150px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          '&:hover': {
            backgroundColor: !disabled ? 'secondary.contrastText' : 'inherit',
            color: !disabled ? 'text.secondary' : 'inherit',
            textDecoration: 'none'
          }
        }}
      >
        <Box sx={{ pointerEvents: 'none' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
              gap: '12px'
            }}
          >
            <Box display="flex" flexDirection="column" gap="4px">
              {orientation && !disabled && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '18px',
                    opacity: disabled ? 0.5 : 1,
                    textTransform: 'capitalize'
                  }}
                >
                  {`${orientation} format`}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: disabled ? '18px' : '16px',
                  opacity: disabled ? 0.5 : 1
                }}
              >
                {disabled
                  ? 'Complete the form above to upload.'
                  : 'Choose a file.'}
              </Typography>
            </Box>

            <Box sx={{ pointerEvents: disabled ? 'auto' : 'none' }}>
              <span>
                <Button
                  variant="outlined"
                  disabled={disabled}
                  sx={{ opacity: disabled ? 0.5 : 1 }}
                  data-testid="upload-button"
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontWeight: 600, opacity: disabled ? 0.5 : 1 }}
                  >
                    Upload New
                  </Typography>
                </Button>
              </span>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default AssetUploader
